import React, { useState, useEffect } from 'react';
import {
  ArrowLeft,
  Scan,
  Navigation,
  Edit3,
  Eye,
  ChevronDown,
  Receipt,
  Megaphone,
  CircleX,
  AlertCircle,
  WifiOff,
} from 'lucide-react';
import { Button, EmptyState, Inline, Loading } from '@relay-towing/ui';
import VinScannerScreen from './VinScannerScreen';
import { InspectionSheet } from '@/components/inspection-sheet';
import { useUpdateDispatchStatus } from '@/hooks/useUpdateDispatchStatus';
import { useUpdateDriverStatus } from '@/hooks/useUpdateDriverStatus';
import { useReportGoa } from '@/hooks/useReportGoa';
import { useCancelUrgentlyCall } from '@/hooks/useCancelUrgentlyCall';
import { useUpdateVehicle } from '@/hooks/useUpdateVehicle';
import { useGetVehicleMakes } from '@/api/hooks/useGetVehicleMakes';
import { useGetVehicleModels } from '@/api/hooks/useGetVehicleModels';
import { isEmbeddedMode } from '@/utils/embeddedMode';
import { useVehicleMediaUpload } from '@/hooks/useVehicleMediaUpload';

import { useDriverCall } from '@/hooks/useDriverCall';
import { useDriverProfile } from '@/hooks/useDriverProfile';
import { useHybridAuth } from '@/hooks/useHybridAuth';
import AuthErrorScreen from '@/components/auth/AuthErrorScreen';
import { authErrorKindFromError } from '@/utils/authErrorKind';
import { useVehicleImages } from '@/hooks/useVehicleImages';
import { useCreateImpound } from '@/hooks/useCreateImpound';
import { completesIntoImpound } from './completion';
import { usePlan } from '@/hooks/usePlan';
import { useHasPermission } from '@/hooks/useDriverPermissions';
import ImpoundConfirmationPopup from '@/components/common/ImpoundConfirmationPopup';
import UrgentlyCompletionModal from '@/components/common/UrgentlyCompletionModal';
import UrgentlyGoaModal from '@/components/common/UrgentlyGoaModal';
import UrgentlyCancelModal from '@/components/common/UrgentlyCancelModal';
import VehicleNotesSection from '@/components/common/VehicleNotesSection';
import SlideShowModal from '@/components/common/SlideShowModal';
import VehicleMediaCarousel from '@/components/common/VehicleMediaCarousel';
import LocationRoute from '@/components/common/LocationRoute';
import { DocumentTypeSelector } from '@/components/common/DocumentTypeSelector';
import { DocumentType } from '@/types/document';
import { useToast } from '@/contexts/ToastContext';

// Status mapping functions
const getStatusColor = (status: string) => {
  // Convert backend status to frontend status
  const statusMap: { [key: string]: string } = {
    NEW: 'ready',
    PENDING: 'waiting',
    DISPATCHED: 'en_route',
    ON_SCENE: 'on_scene',
    TOWING: 'towing',
    COMPLETED: 'complete',
    COMPLETED_AWAITING: 'complete',
    GOA: 'complete',
    GOA_AWAITING: 'complete',
    FAILED: 'canceled',
    CANCELED: 'canceled',
  };

  const mappedStatus = statusMap[status] || status;

  switch (mappedStatus) {
    case 'ready':
      return 'bg-green-600';
    case 'waiting':
      return 'bg-yellow-600';
    case 'en_route':
      return 'bg-blue-600';
    case 'on_scene':
      return 'bg-orange-600';
    case 'towing':
      return 'bg-teal-600';
    case 'complete':
      return 'bg-purple-600';
    case 'canceled':
      return 'bg-gray-600';
    default:
      return 'bg-gray-600';
  }
};

const getStatusDisplay = (status: string) => {
  if (status === 'FAILED' || status === 'CANCELED') return 'Canceled';
  // Handle GOA and GOA_AWAITING specially - display as "GOA"
  if (status === 'GOA' || status === 'GOA_AWAITING') {
    return 'GOA';
  }

  // Convert backend status to frontend status
  const statusMap: { [key: string]: string } = {
    NEW: 'ready',
    PENDING: 'waiting',
    DISPATCHED: 'en_route',
    ON_SCENE: 'on_scene',
    TOWING: 'towing',
    COMPLETED: 'complete',
    COMPLETED_AWAITING: 'complete',
  };

  const mappedStatus = statusMap[status] || status;

  switch (mappedStatus) {
    case 'ready':
      return 'Ready';
    case 'waiting':
      return 'Waiting';
    case 'en_route':
      return 'En Route';
    case 'on_scene':
      return 'On Scene';
    case 'towing':
      return 'Towing';
    case 'complete':
      return 'Complete';
    default:
      return 'Unknown';
  }
};

// Get the approval status text for URGENTLY calls
const getUrgentlyApprovalStatus = (status: string) => {
  switch (status) {
    case 'COMPLETED_AWAITING':
      return 'Awaiting Approval';
    case 'GOA_AWAITING':
      return 'GOA Awaiting Approval';
    case 'COMPLETED':
      return 'Completion Approved';
    case 'GOA':
      return 'GOA Approved';
    default:
      return null;
  }
};

interface CallScreenProps {
  callId: string;
  onBack: () => void;
  onImpoundCreated?: (impoundId: string) => void; // Add callback for impound creation
  onNavigateToPayment?: (dispatchId: string) => void; // Add callback for payment navigation
  showHeader?: boolean;
}

/**
 * What to search for when the driver taps the pickup. A website booking made
 * with "Use my location" and no typed address has only a GPS point, and its
 * pickup text ("Not provided — call customer", or "GPS <lat>, <lng>") is not
 * something a maps app can find, so the point itself is used.
 */
function navigationTarget(call: {
  pickupLocation?: string | null;
  pickupLocationLat?: number | null;
  pickupLocationLong?: number | null;
}): string {
  const text = (call.pickupLocation ?? '').trim();
  const placeholder =
    text === '' || /^not provided/i.test(text) || /^GPS -?\d/.test(text);
  const lat = call.pickupLocationLat;
  const lng = call.pickupLocationLong;
  const hasPoint =
    typeof lat === 'number' &&
    typeof lng === 'number' &&
    Number.isFinite(lat) &&
    Number.isFinite(lng) &&
    !(lat === 0 && lng === 0);
  return placeholder && hasPoint ? `${lat},${lng}` : text;
}

const CallScreen: React.FC<CallScreenProps> = ({
  callId,
  onBack,
  onImpoundCreated,
  onNavigateToPayment,
  showHeader = true,
}) => {
  const {
    data: driverCallData,
    error: callError,
    fetchStatus: callFetchStatus,
    refetch: refetchCall,
  } = useDriverCall(callId);
  const { isAuthenticated, isLoading: isAuthLoading } = useHybridAuth();
  const {
    data: driverProfile,
    error: profileError,
    refetch: refetchProfile,
  } = useDriverProfile();
  const {
    vehicleImages = [],
    isLoading: isImagesLoading,
    uploadImage,
    deleteImage,
  } = useVehicleImages(driverCallData?.vehicle?.id);

  const { includes, isLoading: isVideoPermissionsLoading } = usePlan();
  const canUploadVideo = includes('video');

  const { showToast } = useToast();

  const {
    uploadProgress,
    uploadingFilesCount,
    handleTakePhoto: triggerUpload,
  } = useVehicleMediaUpload({
    vehicleId: driverCallData?.vehicle?.id,
    uploadImage,
    onError: (msg) => showToast('error', msg, 3000),
  });

  // Check if user can create invoices
  const canCreateInvoice = useHasPermission('invoices.create');
  const canCreateImpounds = useHasPermission('impounds.create');
  const canEditStatus = useHasPermission('drivers.edit_status');

  // Check if we're in embedded mode
  const isEmbedded = isEmbeddedMode();

  const [showVinScanner, setShowVinScanner] = useState(false);
  const [scannedVin, setScannedVin] = useState<string | null>(null);
  const [showSlideShow, setShowSlideShow] = useState(false);
  const [slideShowIndex, setSlideShowIndex] = useState(0);
  const [showDocumentTypeSelector, setShowDocumentTypeSelector] =
    useState(false);

  // Vehicle edit state
  const [isEditing, setIsEditing] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [formData, setFormData] = React.useState({
    vehicleName: '',
    vehicleModel: '',
    vehicleYear: 0,
    vehicleColor: '',
    vehicleOdometer: null as number | null,
    licensePlate: '',
    vin: '',
  });

  // Vehicle makes and models data
  const { data: vehicleMakesData } = useGetVehicleMakes();
  const { data: vehicleModelsData } = useGetVehicleModels(
    { make: formData.vehicleName },
    { enabled: !!formData.vehicleName }
  );

  // Impound confirmation state
  const [showImpoundConfirmation, setShowImpoundConfirmation] = useState(false);

  // URGENTLY completion modal state
  const [showUrgentlyCompletionModal, setShowUrgentlyCompletionModal] =
    useState(false);
  const [pendingCompletionData, setPendingCompletionData] = useState<{
    dispatchId: string;
    status: string;
    version?: number;
  } | null>(null);

  // GOA (Gone On Arrival) modal state
  const [showGoaModal, setShowGoaModal] = useState(false);

  // Cancel Job modal state
  const [showCancelModal, setShowCancelModal] = useState(false);

  // Status update mutations
  const updateDispatchStatusMutation = useUpdateDispatchStatus();
  const updateDriverStatusMutation = useUpdateDriverStatus();
  const updateVehicleMutation = useUpdateVehicle();
  const createImpoundMutation = useCreateImpound();
  const reportGoaMutation = useReportGoa();
  const cancelUrgentlyCallMutation = useCancelUrgentlyCall();

  // Status dropdown state
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);

  // Status update state
  const [pendingStatusUpdate, setPendingStatusUpdate] = useState<{
    status: string;
    timestamp: number;
  } | null>(null);

  // Initialize form data when call data loads
  useEffect(() => {
    if (driverCallData) {
      const vehicle = driverCallData.vehicle || {};
      const vehicleAttributes = vehicle.vehicleAttributes || {};

      setFormData({
        vehicleName: vehicle.vehicleName || '',
        vehicleModel: vehicle.vehicleModel || '',
        vehicleYear: vehicle.vehicleYear || 0,
        vehicleColor: vehicleAttributes.color || '',
        vehicleOdometer: vehicle.odometer ?? null,
        licensePlate: vehicle.licensePlate || '',
        vin: vehicle.vin || '',
      });
    }
  }, [driverCallData]);

  // Handle status update timeout
  useEffect(() => {
    if (pendingStatusUpdate) {
      const timeoutId = setTimeout(() => {
        console.log('Status update timed out after 30 seconds');
        setPendingStatusUpdate(null);
      }, 30000); // 30 seconds

      return () => clearTimeout(timeoutId);
    }
  }, [pendingStatusUpdate]);

  const handleInputChange = (field: string, value: string | number | null) => {
    // Auto-capitalize VIN and license plate
    if (
      (field === 'vin' || field === 'licensePlate') &&
      typeof value === 'string'
    ) {
      value = value.toUpperCase();
    }
    setFormData((prev) => ({ ...prev, [field]: value }));
    setHasChanges(true);
    setSaveError(null);
    setSaveSuccess(false);
  };

  const handleSave = async () => {
    // Get vehicle ID from the vehicle object
    const vehicleId = driverCallData?.vehicle?.id;
    if (!vehicleId) {
      setSaveError('No vehicle ID found');
      return;
    }

    try {
      setSaveError(null);
      const vehicleAttributes = {
        color: formData.vehicleColor,
      };

      const updateData = {
        vehicleName: formData.vehicleName,
        vehicleModel: formData.vehicleModel,
        ...(formData.vehicleYear > 0 && { vehicleYear: formData.vehicleYear }),
        licensePlate: formData.licensePlate,
        vin: formData.vin,
        odometer: formData.vehicleOdometer || undefined,
        vehicleAttributes,
      };

      console.log('🚗 [CallScreen] Attempting to update vehicle:', {
        vehicleId,
        updateData,
        formData,
      });

      const result = await updateVehicleMutation.mutateAsync({
        vehicleId,
        updateData,
      });

      console.log('🚗 [CallScreen] Vehicle update result:', result);

      setHasChanges(false);
      setIsEditing(false);
      setSaveSuccess(true);

      // Clear success message after 3 seconds
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (error: any) {
      console.error('🚗 [CallScreen] Failed to update vehicle:', error);
      setSaveError(error.message || 'Failed to save changes');
    }
  };

  const handleCancel = () => {
    if (driverCallData) {
      const vehicle = driverCallData.vehicle || {};
      const vehicleAttributes = vehicle.vehicleAttributes || {};

      setFormData({
        vehicleName: vehicle.vehicleName || '',
        vehicleModel: vehicle.vehicleModel || '',
        vehicleYear: vehicle.vehicleYear || 0,
        vehicleColor: vehicleAttributes.color || '',
        vehicleOdometer: vehicle.odometer ?? null,
        licensePlate: vehicle.licensePlate || '',
        vin: vehicle.vin || '',
      });
    }

    setHasChanges(false);
    setIsEditing(false);
    setSaveError(null);
    setSaveSuccess(false);
    setScannedVin(null); // Reset scanned VIN when cancelling
  };

  const handleScanVin = () => {
    console.log('Scan VIN clicked');
    setShowVinScanner(true);
  };

  const handleRescanVin = () => {
    console.log('Rescan VIN clicked');
    setShowVinScanner(true);
  };

  const handleVinScannerClose = () => {
    setShowVinScanner(false);
  };

  const handleVinScanned = (vin: string) => {
    setScannedVin(vin);
    setFormData((prev) => ({ ...prev, vin }));
    setHasChanges(true);
    setSaveError(null);
    setSaveSuccess(false);
    setShowVinScanner(false);
  };

  const handleStartCall = async () => {
    if (!driverCallData?.id) return;

    try {
      // Update dispatch status to DISPATCHED
      await updateDispatchStatusMutation.mutateAsync({
        dispatchId: driverCallData.id,
        status: 'DISPATCHED',
        version: driverCallData.version, // Include the current version
      });

      // Try to update driver status, but don't fail if it doesn't work.
      // Without drivers.edit_status the backend refuses it, so do not ask.
      if (canEditStatus) {
        try {
          await updateDriverStatusMutation.mutateAsync('ACTIVE');
          console.log('Driver status updated successfully');
        } catch (driverError) {
          console.warn(
            'Failed to update driver status (non-critical):',
            driverError
          );
        }
      }

      console.log('Call started successfully');
    } catch (error: any) {
      console.error('Failed to start call:', error);

      // Check if it's a version conflict error
      if (error.message?.includes('Version conflict')) {
        // Show toast message
        showToast(
          'warning',
          'This call was just updated by someone else. Please try again.',
          5000
        );
      } else {
        // Show error in UI
        setSaveError('Failed to start call. Please try again.');
      }
    }
  };

  const handleTakePhoto = () => {
    if (!driverCallData?.vehicle?.id) return;

    // For URGENTLY dispatches, show document type selector first
    if (driverCallData?.source === 'URGENTLY') {
      setShowDocumentTypeSelector(true);
      return;
    }

    triggerUpload();
  };

  const handleDocumentTypeSelected = (type: DocumentType) => {
    setShowDocumentTypeSelector(false);
    setTimeout(() => triggerUpload({ documentType: type }), 100);
  };

  const handleDocumentTypeSelectorDismiss = () => {
    setShowDocumentTypeSelector(false);
  };

  const removePhoto = async (index: number) => {
    const imageUrl = vehicleImages[index];
    if (!imageUrl || !driverCallData?.vehicle?.id) return;

    try {
      await deleteImage.mutateAsync({
        vehicleId: driverCallData.vehicle.id,
        imageUrl,
      });

      // Close the slideshow if there are no more images after deletion
      if (vehicleImages.length <= 1) {
        setShowSlideShow(false);
      } else if (slideShowIndex >= vehicleImages.length - 1) {
        // If we deleted the last image, go back one index
        setSlideShowIndex(Math.max(0, slideShowIndex - 1));
      }
    } catch (error) {
      console.error('Failed to delete image:', error);
      showToast('error', 'Failed to delete image. Please try again.', 3000);
    }
  };

  const handleAddressClick = () => {
    if (!driverCallData) return;
    const address = navigationTarget(driverCallData);
    if (!address) return;

    // Check if we're in embedded mode and can use native interface
    if (
      isEmbedded &&
      typeof (window as any).ReactNativeWebView !== 'undefined'
    ) {
      console.log('📱 [CallScreen] Opening address in native maps:', address);

      // Check if we have the iOS-specific openMaps method
      if (typeof (window as any).ReactNativeWebView.openMaps === 'function') {
        console.log('📱 [CallScreen] Using iOS native maps interface');
        (window as any).ReactNativeWebView.openMaps(address);
        return;
      }

      // Fallback to Android-style message for Android or generic embedded mode
      console.log('📱 [CallScreen] Using Android-style message interface');
      (window as any).ReactNativeWebView.postMessage(
        JSON.stringify({
          type: 'OPEN_MAPS',
          data: {
            address: address,
          },
        })
      );
      return;
    }

    // Fallback to web-based maps for non-embedded mode
    const encodedAddress = encodeURIComponent(address);

    // Detect device OS
    const userAgent =
      navigator.userAgent || navigator.vendor || (window as any).opera;
    const isIOS =
      /iPad|iPhone|iPod/.test(userAgent) && !(window as any).MSStream;
    const isAndroid = /android/i.test(userAgent);

    let mapsUrl: string;

    if (isIOS) {
      // Use Apple Maps on iOS
      mapsUrl = `maps://?q=${encodedAddress}`;
    } else if (isAndroid) {
      // Use Google Maps on Android
      mapsUrl = `geo:0,0?q=${encodedAddress}`;
    } else {
      // Fallback to Google Maps web for other platforms
      mapsUrl = `https://maps.google.com/maps?q=${encodedAddress}`;
    }

    // Open in native maps app or browser
    window.open(mapsUrl, '_blank');
  };

  const handleCallStatusChange = async (callId: string, newStatus: string) => {
    if (!callId) return;

    // If the new status is COMPLETED and the call is marked as impound, show the impound confirmation popup
    const offerImpound = completesIntoImpound(
      driverCallData?.isImpound,
      canCreateImpounds
    );
    if (newStatus === 'COMPLETED' && offerImpound) {
      setShowImpoundConfirmation(true);
      return;
    }

    // If the new status is COMPLETED and source is URGENTLY, show the URGENTLY completion modal
    if (newStatus === 'COMPLETED' && driverCallData?.source === 'URGENTLY') {
      setPendingCompletionData({
        dispatchId: callId,
        status: newStatus,
        version: driverCallData?.version,
      });
      setShowUrgentlyCompletionModal(true);
      return;
    }

    // If the new status is COMPLETED but it's not an impound call or URGENTLY call, complete it directly
    if (newStatus === 'COMPLETED' && !offerImpound) {
      try {
        await updateDispatchStatusMutation.mutateAsync({
          dispatchId: callId,
          status: newStatus,
          version: driverCallData?.version,
        });

        // Navigate back to home screen after successful completion
        onBack();
        return;
      } catch (error: any) {
        console.error('Failed to complete call:', error);

        // Check if it's a version conflict error
        if (error.message?.includes('Version conflict')) {
          showToast(
            'warning',
            'This call was just updated by someone else. The latest information has been loaded.',
            5000
          );
        } else {
          setSaveError(
            `Failed to complete call: ${error.message || 'Unknown error'}`
          );
        }
        return;
      }
    }

    // Set optimistic update state
    setPendingStatusUpdate({
      status: newStatus,
      timestamp: Date.now(),
    });

    try {
      // Include the current version in the update
      await updateDispatchStatusMutation.mutateAsync({
        dispatchId: callId,
        status: newStatus,
        version: driverCallData?.version, // Include the current version
      });

      // Clear pending state on success
      setPendingStatusUpdate(null);
      setSaveError(null);
      setSaveSuccess(false);
    } catch (error: any) {
      console.error('Failed to update call status:', error);

      // Check if it's a version conflict error
      if (error.message?.includes('Version conflict')) {
        // Show toast message
        showToast(
          'warning',
          'This call was just updated by someone else. The latest information has been loaded.',
          5000
        );

        // No need to set save error as we're showing a toast
        setSaveError(null);
      } else {
        // For other errors, show in the UI
        const statusDisplay = getStatusDisplay(newStatus);
        setSaveError(`Failed to update call status to ${statusDisplay}`);
      }

      // Clear pending state on error
      setPendingStatusUpdate(null);
    }
  };

  const handleCreateImpound = async () => {
    if (!driverCallData?.id) return;

    try {
      // First update the call status to COMPLETED
      await updateDispatchStatusMutation.mutateAsync({
        dispatchId: driverCallData.id,
        status: 'COMPLETED',
        version: driverCallData.version, // Include the current version
      });

      // Then create the impound
      try {
        const result = await createImpoundMutation.mutateAsync({
          dispatchId: driverCallData.id,
          storageLot: 'Main Yard', // Default storage lot
        });

        console.log('Impound created successfully:', result);

        // Close the confirmation popup
        setShowImpoundConfirmation(false);

        // Navigate to the impound screen using the callback
        if (onImpoundCreated) {
          onImpoundCreated(result.id);
        } else {
          // Fallback if callback is not provided
          console.warn('onImpoundCreated callback not provided');
          onBack(); // Go back to home screen
        }
      } catch (impoundError: any) {
        console.error('Failed to create impound:', impoundError);
        setSaveError(
          `Failed to create impound: ${impoundError.message || 'Unknown error'}`
        );
        setShowImpoundConfirmation(false);

        // Even if impound creation fails, the call is still completed
        // so we should navigate back to the home screen
        setTimeout(() => onBack(), 3000);
      }
    } catch (error: any) {
      console.error('Failed to complete call:', error);

      // Check if it's a version conflict error
      if (error.message?.includes('Version conflict')) {
        // Show toast message
        showToast(
          'warning',
          'This call was just updated by someone else. Please try again.',
          5000
        );
        setShowImpoundConfirmation(false);
      } else {
        setSaveError(
          `Failed to complete call: ${error.message || 'Unknown error'}`
        );
        setShowImpoundConfirmation(false);
      }
    }
  };

  const handleCancelImpound = async () => {
    if (!driverCallData?.id) return;

    try {
      // Just update the call status to COMPLETED without creating an impound
      await updateDispatchStatusMutation.mutateAsync({
        dispatchId: driverCallData.id,
        status: 'COMPLETED',
        version: driverCallData.version, // Include the current version
      });

      // Close the confirmation popup and go back to the home screen
      setShowImpoundConfirmation(false);
      onBack();
    } catch (error: any) {
      console.error('Failed to complete call:', error);

      // Check if it's a version conflict error
      if (error.message?.includes('Version conflict')) {
        // Show toast message
        showToast(
          'warning',
          'This call was just updated by someone else. Please try again.',
          5000
        );
        setShowImpoundConfirmation(false);
      } else {
        setSaveError('Failed to complete call');
        setShowImpoundConfirmation(false);
      }
    }
  };

  const handleUrgentlyCompletionSubmit = async (
    towRequired: boolean,
    storageRequired: boolean
  ) => {
    if (!pendingCompletionData) return;

    try {
      await updateDispatchStatusMutation.mutateAsync({
        dispatchId: pendingCompletionData.dispatchId,
        status: pendingCompletionData.status,
        version: pendingCompletionData.version,
        additionalInfo: {
          towRequired,
          storageRequired,
        },
      });

      // Close the modal and navigate back
      setShowUrgentlyCompletionModal(false);
      setPendingCompletionData(null);
      onBack();
    } catch (error: any) {
      console.error('Failed to complete URGENTLY call:', error);

      // Check if it's a version conflict error
      if (error.message?.includes('Version conflict')) {
        showToast(
          'warning',
          'This call was just updated by someone else. Please try again.',
          5000
        );
      } else {
        setSaveError(
          `Failed to complete call: ${error.message || 'Unknown error'}`
        );
      }

      setShowUrgentlyCompletionModal(false);
      setPendingCompletionData(null);
    }
  };

  const handleUrgentlyCompletionCancel = () => {
    setShowUrgentlyCompletionModal(false);
    setPendingCompletionData(null);
  };

  const handleReportGoaClick = () => {
    setShowGoaModal(true);
  };

  const handleGoaSubmit = async (goaReason: string, notes?: string) => {
    try {
      await reportGoaMutation.mutateAsync({
        dispatchId: callId,
        goaReason,
        notes,
      });

      // Show success toast
      showToast('success', 'GOA reported successfully', 3000);

      // Close the modal and navigate back
      setShowGoaModal(false);
      onBack();
    } catch (error: any) {
      console.error('Failed to report GOA:', error);
      showToast(
        'error',
        `Failed to report GOA: ${error.message || 'Unknown error'}`,
        5000
      );
    }
  };

  const handleGoaCancel = () => {
    setShowGoaModal(false);
  };

  const handleCancelJobClick = () => {
    setShowCancelModal(true);
  };

  const handleCancelSubmit = async (cancelReason: string, notes?: string) => {
    if (!driverCallData?.id || !driverProfile?.companyId) {
      showToast('error', 'Missing required data to cancel the job', 3000);
      return;
    }

    try {
      await cancelUrgentlyCallMutation.mutateAsync({
        dispatchId: driverCallData.id,
        companyId: driverProfile.companyId,
        cancelReason,
        notes,
      });

      // Show success toast
      showToast('success', 'Job cancelled successfully', 3000);

      // Close the modal and navigate back
      setShowCancelModal(false);
      onBack();
    } catch (error: any) {
      console.error('Failed to cancel job:', error);
      showToast(
        'error',
        `Failed to cancel job: ${error.message || 'Unknown error'}`,
        5000
      );
    }
  };

  const handleCancelCancel = () => {
    setShowCancelModal(false);
  };

  // Set up Android navigation callbacks
  useEffect(() => {
    // Only set up navigation in embedded mode
    if (typeof (window as any).ReactNativeWebView !== 'undefined') {
      console.log('📱 [CallScreen] Setting up Android navigation callbacks');

      (window as any).navigateBack = function () {
        console.log(
          '📱 [CallScreen] navigateBack called - going back to HomeScreen'
        );
        onBack();
      };

      (window as any).navigateForward = function () {
        console.log(
          '📱 [CallScreen] navigateForward called - no action (forward not applicable)'
        );
        // Do nothing - forward navigation not applicable on CallScreen
      };

      // Cleanup function
      return () => {
        console.log('📱 [CallScreen] Cleaning up Android navigation callbacks');
        delete (window as any).navigateBack;
        delete (window as any).navigateForward;
      };
    }
  }, [onBack]);

  const callShell = (body: React.ReactNode) => (
    <div className="min-h-screen bg-primary responsive-container mx-auto flex flex-col">
      {showHeader && (
        <div className="p-4 flex items-center gap-3">
          <button
            onClick={onBack}
            className="p-2 hover:bg-background/10 rounded-lg transition-colors"
          >
            <ArrowLeft size={24} className="text-white" />
          </button>
          <h1 className="text-xl font-semibold text-white">Call Details</h1>
        </div>
      )}

      <div className="bg-background flex-1 rounded-t-3xl">
        <div className="p-6 flex items-center justify-center min-h-96">
          <div className="text-center">{body}</div>
        </div>
      </div>
    </div>
  );

  // The query is disabled until the session, profile and permissions are
  // known, and paused while the device is offline. React Query reports both
  // as "no data, not loading", which this screen once read as a failed
  // fetch and showed "Failed to load call" without sending a request. Tell
  // those states apart, and only call it a failure when a request failed.
  if (!driverCallData) {
    if (!isAuthenticated && !isAuthLoading) {
      return <AuthErrorScreen kind="session-expired" />;
    }

    // The call query waits on the profile. If that fetch failed the call
    // never runs, so its error is the one to show, or this spins forever.
    const blockingError = callError ?? (driverProfile ? null : profileError);
    const retry = () => (callError ? refetchCall() : refetchProfile());

    if (blockingError) {
      const authKind = authErrorKindFromError(blockingError);
      if (authKind) {
        return (
          <AuthErrorScreen
            kind={authKind}
            errorMessage={blockingError.message}
            onRetry={retry}
          />
        );
      }

      const status = (blockingError as { status?: number }).status;
      return callShell(
        <EmptyState
          icon={AlertCircle}
          title={status === 404 ? 'Call not found' : 'Failed to load call'}
          description={
            status === 404
              ? 'This call may have been deleted or moved to another company.'
              : blockingError.message || 'Please try again.'
          }
          action={
            <Inline gap={3} justify="center">
              <Button onClick={retry}>Try again</Button>
              <Button variant="outline" onClick={onBack}>
                Go back
              </Button>
            </Inline>
          }
        />
      );
    }

    if (callFetchStatus === 'paused') {
      return callShell(
        <EmptyState
          icon={WifiOff}
          title="You are offline"
          description="The call will load when the connection is back."
          action={<Button onClick={() => refetchCall()}>Try again</Button>}
        />
      );
    }

    return callShell(<Loading title="Loading call details..." />);
  }

  // ACCEPTED_AWAITING calls are not actionable by drivers - show unavailable message
  if (driverCallData?.status === 'ACCEPTED_AWAITING') {
    return (
      <div className="min-h-screen bg-primary responsive-container mx-auto flex flex-col">
        {showHeader && (
          <div className="p-4 flex items-center gap-3">
            <button
              onClick={onBack}
              className="p-2 hover:bg-background/10 rounded-lg transition-colors"
            >
              <ArrowLeft size={24} className="text-white" />
            </button>
            <h1 className="text-xl font-semibold text-white">Call Details</h1>
          </div>
        )}

        <div className="bg-background flex-1 rounded-t-3xl">
          <div className="p-6 flex items-center justify-center min-h-96">
            <div className="text-center">
              <p className="text-gray-600 text-lg mb-2">
                This call is not available
              </p>
              <p className="text-gray-500 text-sm">
                This call is awaiting dispatch and cannot be viewed yet.
              </p>
              <button
                onClick={onBack}
                className="mt-4 bg-primary text-primary-foreground px-4 py-2 rounded-lg hover:bg-primary/90 transition-colors"
              >
                Go Back
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show VinScannerScreen if scanner is active
  if (showVinScanner) {
    return (
      <VinScannerScreen
        onClose={handleVinScannerClose}
        onVinScanned={handleVinScanned}
        vehicleId={driverCallData.vehicle?.id || ''}
      />
    );
  }

  return (
    <div className="min-h-screen bg-background responsive-container mx-auto flex flex-col">
      {/* Header */}
      {showHeader && (
        <div className="bg-primary text-primary-foreground px-4 py-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <button
                onClick={onBack}
                className="mr-3 p-1 hover:bg-background/10 rounded transition-colors"
              >
                <ArrowLeft size={24} />
              </button>
              <div className="flex items-center text-2xl" onClick={onBack}>
                <span className="mr-2">Back</span>
              </div>
            </div>

            <div className="flex items-center gap-2">
              {/* Payment/Invoice Icon Button - only shows when user has create invoice permission and source is not URGENTLY */}
              {canCreateInvoice &&
                onNavigateToPayment &&
                driverCallData.source !== 'URGENTLY' && (
                  <button
                    onClick={() => onNavigateToPayment(driverCallData.id)}
                    className="p-2 hover:bg-background/10 rounded transition-colors"
                    title="Collect Payment"
                  >
                    <Receipt size={24} />
                  </button>
                )}

              {/* Save Button - appears when there are vehicle changes (not status changes) */}
              {hasChanges && (
                <>
                  <button
                    onClick={handleCancel}
                    className="px-3 py-1 text-sm text-white border border-white border-opacity-50 rounded-lg hover:bg-background/10 transition-colors"
                    disabled={updateVehicleMutation.isPending}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSave}
                    className="px-4 py-2 text-sm bg-background text-primary rounded-lg font-medium hover:bg-gray-100 transition-colors disabled:opacity-50"
                    disabled={updateVehicleMutation.isPending}
                  >
                    {updateVehicleMutation.isPending ? 'Saving...' : 'Save'}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Content */}
      <div
        className={`flex-1 px-4 py-4 ${
          isEmbedded
            ? 'min-h-[600px]' // Fixed height for WebView compatibility
            : '' // No fixed height for normal web mode
        }`}
      >
        {/* Vehicle Photos Carousel */}
        <VehicleMediaCarousel
          vehicleImages={vehicleImages}
          isLoading={isImagesLoading}
          uploadProgress={uploadProgress}
          uploadingFilesCount={uploadingFilesCount}
          isVideoPermissionsLoading={isVideoPermissionsLoading}
          canUploadVideo={canUploadVideo}
          onImageClick={(index) => {
            setSlideShowIndex(index);
            setShowSlideShow(true);
          }}
          onTakePhoto={handleTakePhoto}
          uploadButtonText={canUploadVideo ? 'Add Media' : 'Add Photo'}
          screenName="CallScreen"
        />

        {/* Location Route (Pickup and Destination) */}
        <LocationRoute
          pickupLocation={driverCallData.pickupLocation}
          destination={driverCallData.destination}
          onPickupClick={handleAddressClick}
          onDestinationClick={handleAddressClick}
          callStatus={driverCallData.status}
        />

        {/* Call Status Dropdown */}
        <div className="mb-4 relative">
          {pendingStatusUpdate ? (
            <div className="w-full flex items-center justify-center px-4 py-2 bg-background border border-gray-200 rounded-lg">
              <div className="flex items-center gap-2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
                <span className="text-sm font-medium text-gray-700">
                  Posting status change...
                </span>
              </div>
            </div>
          ) : (
            <button
              onClick={() => setShowStatusDropdown(!showStatusDropdown)}
              className="w-full flex items-center justify-between px-4 py-2 bg-background border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center">
                <div
                  className={`w-2 h-2 rounded-full mr-2 ${getStatusColor(driverCallData.status)}`}
                ></div>
                <span className="text-sm font-medium text-gray-700">
                  {getStatusDisplay(driverCallData.status)}
                </span>
              </div>
              <ChevronDown
                size={14}
                className={`text-gray-400 transition-transform ${showStatusDropdown ? 'rotate-180' : ''}`}
              />
            </button>
          )}

          {/* Status Dropdown Menu */}
          {showStatusDropdown && !pendingStatusUpdate && (
            <div className="absolute left-0 right-0 top-full mt-1 bg-background border border-gray-200 rounded-xl shadow-xl z-50">
              {/* All available statuses - drivers can change to any status */}
              {[
                { frontend: 'new', backend: 'NEW' },
                { frontend: 'en_route', backend: 'DISPATCHED' },
                { frontend: 'on_scene', backend: 'ON_SCENE' },
                { frontend: 'towing', backend: 'TOWING' },
                { frontend: 'complete', backend: 'COMPLETED' },
              ]
                .filter(({ backend }) => {
                  // Hide current status and GOA statuses from options
                  return backend !== driverCallData.status && backend !== 'GOA';
                })
                .map(({ frontend, backend }) => (
                  <button
                    key={frontend}
                    onClick={() => {
                      handleCallStatusChange(driverCallData.id, backend);
                      setShowStatusDropdown(false);
                    }}
                    className="w-full text-left px-3 py-2 hover:bg-gray-50 transition-colors first:rounded-t-xl last:rounded-b-xl flex items-center"
                  >
                    <div
                      className={`w-2 h-2 rounded-full mr-2 ${getStatusColor(backend)}`}
                    ></div>
                    <span className="text-sm text-gray-700">
                      {getStatusDisplay(backend)}
                    </span>
                  </button>
                ))}
            </div>
          )}
        </div>

        {/* Times */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <p className="text-sm text-gray-500">Dispatched:</p>
            <p className="font-medium text-black">
              {new Date(driverCallData.createdAt).toLocaleTimeString([], {
                hour: 'numeric',
                minute: '2-digit',
              })}
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Finished:</p>
            <p className="font-medium text-black">
              {driverCallData.completionTime
                ? new Date(driverCallData.completionTime).toLocaleTimeString(
                    [],
                    { hour: 'numeric', minute: '2-digit' }
                  )
                : '-'}
            </p>
          </div>
        </div>

        {/* Urgently Section - Only show if source is URGENTLY */}
        {driverCallData.source === 'URGENTLY' && (
          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <div className="flex items-center gap-2 mb-4">
              <h3 className="text-lg font-semibold text-black">Urgently</h3>
              {getUrgentlyApprovalStatus(driverCallData.status) && (
                <span className="text-sm font-medium text-gray-600">
                  {getUrgentlyApprovalStatus(driverCallData.status)}
                </span>
              )}
            </div>

            {/* Inspection Section */}
            <div className="mb-4 space-y-2">
              <InspectionSheet dispatchId={callId} />
            </div>

            {/* Report GOA Button - Only show if status is NOT COMPLETED, GOA, or GOA_AWAITING */}
            {driverCallData.status !== 'COMPLETED' &&
              driverCallData.status !== 'GOA' &&
              driverCallData.status !== 'GOA_AWAITING' && (
                <button
                  onClick={handleReportGoaClick}
                  disabled={reportGoaMutation.isPending}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-orange-500 text-white rounded-lg font-medium hover:bg-orange-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Megaphone size={18} />
                  Report GOA
                </button>
              )}

            {/* Cancel Job Button - Only show if status is NOT COMPLETED, GOA, or GOA_AWAITING */}
            {driverCallData.status !== 'COMPLETED' &&
              driverCallData.status !== 'GOA' &&
              driverCallData.status !== 'GOA_AWAITING' && (
                <button
                  onClick={handleCancelJobClick}
                  disabled={cancelUrgentlyCallMutation.isPending}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-black text-white rounded-lg font-medium hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed mt-2"
                >
                  <CircleX size={18} />
                  Cancel Job
                </button>
              )}
          </div>
        )}

        {/* Vehicle Details */}
        <div className="bg-gray-50 rounded-lg p-4 mb-6">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-lg font-semibold text-black">
              Vehicle Information
            </h3>
            <button
              onClick={() => setIsEditing(!isEditing)}
              className="flex items-center gap-1 text-sm text-primary hover:text-opacity-80 transition-colors"
            >
              {isEditing ? (
                <>
                  <Eye size={14} />
                  View
                </>
              ) : (
                <>
                  <Edit3 size={14} />
                  Edit
                </>
              )}
            </button>
          </div>

          {/* Success/Error Messages */}
          {saveSuccess && (
            <div className="mb-3 p-2 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-sm text-green-700">
                ✓ Vehicle information updated successfully
              </p>
            </div>
          )}

          {saveError && (
            <div className="mb-3 p-2 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-700">✗ {saveError}</p>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <p className="text-sm text-gray-500 mb-1">Make</p>
              {isEditing ? (
                <select
                  value={
                    vehicleMakesData?.find(
                      (make) =>
                        make.make.toLowerCase().trim() ===
                        formData.vehicleName?.toLowerCase().trim()
                    )?.make || formData.vehicleName
                  }
                  onChange={(e) => {
                    handleInputChange('vehicleName', e.target.value);
                    // Reset model when make changes
                    handleInputChange('vehicleModel', '');
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-900 bg-background focus:ring-2 focus:ring-primary focus:border-primary"
                >
                  <option value="">Select Make</option>
                  {vehicleMakesData?.map((make) => (
                    <option key={make.make} value={make.make}>
                      {make.make}
                    </option>
                  ))}
                </select>
              ) : (
                <p className="font-medium text-black">
                  {formData.vehicleName || 'N/A'}
                </p>
              )}
            </div>
            <div>
              <p className="text-sm text-gray-500 mb-1">Model</p>
              {isEditing ? (
                <select
                  value={
                    vehicleModelsData?.find(
                      (model) =>
                        model.model.toLowerCase().trim() ===
                        formData.vehicleModel?.toLowerCase().trim()
                    )?.model || formData.vehicleModel
                  }
                  onChange={(e) =>
                    handleInputChange('vehicleModel', e.target.value)
                  }
                  disabled={!formData.vehicleName}
                  className={`w-full px-3 py-2 border border-gray-300 rounded-md text-sm font-medium ${
                    !formData.vehicleName
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : 'text-gray-900 bg-background focus:ring-2 focus:ring-primary focus:border-primary'
                  }`}
                >
                  <option value="">Select Model</option>
                  {vehicleModelsData?.map((model) => (
                    <option key={model.id} value={model.model}>
                      {model.model}
                    </option>
                  ))}
                </select>
              ) : (
                <p className="font-medium text-black">
                  {formData.vehicleModel || 'N/A'}
                </p>
              )}
            </div>
            <div>
              <p className="text-sm text-gray-500 mb-1">Year</p>
              {isEditing ? (
                <input
                  type="text"
                  value={formData.vehicleYear || ''}
                  onChange={(e) => {
                    const value = e.target.value;
                    // Allow empty string or valid numbers
                    if (value === '' || /^\d+$/.test(value)) {
                      handleInputChange(
                        'vehicleYear',
                        value === '' ? 0 : parseInt(value)
                      );
                    }
                  }}
                  placeholder="Enter year"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-900 bg-background focus:ring-2 focus:ring-primary focus:border-primary placeholder-gray-400"
                />
              ) : (
                <p className="font-medium text-black">
                  {formData.vehicleYear || 'N/A'}
                </p>
              )}
            </div>
            <div>
              <p className="text-sm text-gray-500 mb-1">Color</p>
              {isEditing ? (
                <input
                  type="text"
                  value={formData.vehicleColor}
                  onChange={(e) =>
                    handleInputChange('vehicleColor', e.target.value)
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-900 bg-background focus:ring-2 focus:ring-primary focus:border-primary"
                />
              ) : (
                <p className="font-medium text-black">
                  {formData.vehicleColor || 'N/A'}
                </p>
              )}
            </div>
            <div className="col-span-2">
              <p className="text-sm text-gray-500 mb-1">Odometer</p>
              {isEditing ? (
                <input
                  type="number"
                  value={formData.vehicleOdometer || ''}
                  onChange={(e) =>
                    handleInputChange(
                      'vehicleOdometer',
                      e.target.value ? parseInt(e.target.value) : null
                    )
                  }
                  placeholder="Miles"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-900 bg-background focus:ring-2 focus:ring-primary focus:border-primary placeholder-gray-400"
                />
              ) : (
                <p className="font-medium text-black">
                  {formData.vehicleOdometer
                    ? formData.vehicleOdometer.toLocaleString() + ' miles'
                    : 'Not set'}
                </p>
              )}
            </div>
            <div className="col-span-2">
              <p className="text-sm text-gray-500 mb-1">License Plate</p>
              {isEditing ? (
                <input
                  type="text"
                  value={formData.licensePlate}
                  onChange={(e) =>
                    handleInputChange('licensePlate', e.target.value)
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-900 bg-background focus:ring-2 focus:ring-primary focus:border-primary"
                />
              ) : (
                <p className="font-medium text-black">
                  {formData.licensePlate || 'N/A'}
                </p>
              )}
            </div>
          </div>

          <div>
            <p className="text-sm text-gray-500 mb-1">Vehicle VIN</p>
            <div className="flex items-center gap-3">
              {isEditing ? (
                <input
                  type="text"
                  value={formData.vin}
                  onChange={(e) => handleInputChange('vin', e.target.value)}
                  placeholder="Enter 17-character VIN"
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-900 bg-background focus:ring-2 focus:ring-primary focus:border-primary font-mono placeholder-gray-400"
                />
              ) : (
                <div className="flex-1 bg-background border border-gray-300 rounded-md p-3">
                  <p className="font-mono text-sm text-gray-900 font-medium">
                    {scannedVin || formData.vin || 'No VIN available'}
                  </p>
                </div>
              )}
              {/* Scan VIN button - always visible */}
              {scannedVin || formData.vin ? (
                <button
                  onClick={handleRescanVin}
                  className="flex items-center gap-1 px-3 py-2 bg-primary text-primary-foreground text-xs rounded-md hover:bg-primary/90 transition-colors"
                >
                  <Scan size={12} />
                  {isEditing ? 'Rescan' : 'Rescan'}
                </button>
              ) : (
                <button
                  onClick={handleScanVin}
                  className="flex items-center gap-1 px-3 py-2 bg-primary text-primary-foreground text-xs rounded-md hover:bg-primary/90 transition-colors"
                >
                  <Scan size={12} />
                  {isEditing ? 'Scan' : 'Scan VIN'}
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Vehicle Notes Section */}
        <div className="mb-6">
          <VehicleNotesSection
            vehicleId={driverCallData?.vehicle?.id}
            onError={(error) => setSaveError(error)}
          />
        </div>

        {/* Contact Information */}
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-black mb-3">Contact</h3>

          <div className="bg-gray-50 rounded-lg p-4">
            <p className="font-semibold text-black">
              {driverCallData.contactName}
            </p>
            <p className="text-gray-600 mt-1">
              {driverCallData.contactPhone || 'Phone # not provided'}
            </p>
          </div>
        </div>

        {/* Start Call Button */}
        {driverCallData.status === 'NEW' && (
          <div className="mb-6">
            <button
              onClick={handleStartCall}
              disabled={updateDispatchStatusMutation.isPending}
              className="w-full bg-green-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {updateDispatchStatusMutation.isPending ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Starting Call...
                </>
              ) : (
                <>
                  <Navigation size={16} />
                  Start Call
                </>
              )}
            </button>
          </div>
        )}
      </div>

      {/* Impound Confirmation Popup */}
      <ImpoundConfirmationPopup
        isOpen={showImpoundConfirmation}
        onConfirm={handleCreateImpound}
        onCancel={handleCancelImpound}
        isCreating={
          createImpoundMutation.isPending ||
          updateDispatchStatusMutation.isPending
        }
      />

      {/* URGENTLY Completion Modal */}
      <UrgentlyCompletionModal
        isOpen={showUrgentlyCompletionModal}
        onSubmit={handleUrgentlyCompletionSubmit}
        onCancel={handleUrgentlyCompletionCancel}
        isSubmitting={updateDispatchStatusMutation.isPending}
      />

      {/* URGENTLY GOA Modal */}
      <UrgentlyGoaModal
        isOpen={showGoaModal}
        onSubmit={handleGoaSubmit}
        onCancel={handleGoaCancel}
        isSubmitting={reportGoaMutation.isPending}
      />

      {/* URGENTLY Cancel Modal */}
      <UrgentlyCancelModal
        isOpen={showCancelModal}
        onSubmit={handleCancelSubmit}
        onCancel={handleCancelCancel}
        isSubmitting={cancelUrgentlyCallMutation.isPending}
      />

      {/* SlideShowModal */}
      <SlideShowModal
        isOpen={showSlideShow}
        onClose={() => setShowSlideShow(false)}
        images={vehicleImages}
        initialIndex={slideShowIndex}
        onDelete={(index) => removePhoto(index)}
        isDeleting={deleteImage.isPending}
        vehicleId={driverCallData?.vehicle?.id}
      />

      {/* Document Type Selector for URGENTLY dispatches */}
      <DocumentTypeSelector
        isOpen={showDocumentTypeSelector}
        onSelect={handleDocumentTypeSelected}
        onDismiss={handleDocumentTypeSelectorDismiss}
      />
    </div>
  );
};

export default CallScreen;
